import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, CircularProgress, Link, Stack, TextField, Typography } from '@mui/material';
import { ApiError, getPasswordResetRequest } from '@/auth/api';
import { passwordConfirmationError, passwordError } from '@/auth/registration';
import { useAuth } from '@/auth/useAuth';
import { hasQuotationDraft } from '@/features/operations/quotationDraft';
import { NarrowPage } from '@/components/NarrowPage';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';

type PageState = 'loading' | 'ready' | 'invalid' | 'unavailable' | 'changed';
type FieldErrors = { password?: string; passwordConfirmation?: string };

/// What the header says in each state: a dead link has nothing to save (#223, U-1).
const descriptionByState: Record<PageState, string> = {
  loading: 'Usá el enlace que te enviamos por email para elegir tu contraseña nueva.',
  ready: 'Al guardarla, se cierran las sesiones que tenías abiertas en otros dispositivos.',
  invalid: 'Usá el enlace que te enviamos por email para elegir tu contraseña nueva.',
  unavailable: 'Usá el enlace que te enviamos por email para elegir tu contraseña nueva.',
  changed: 'Cerramos las sesiones que tenías abiertas en otros dispositivos.',
};

/**
 * Where somebody who forgot the password chooses a new one, from the link in the mail: buyers
 * since CTA-2, staff since #271. Saving it closes the sessions that were open and signs this
 * one in.
 */
export function CustomerResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const token = searchParams.get('token');
  const [state, setState] = useState<PageState>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [requestError, setRequestError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const started = useRef(false);

  const load = useCallback(() => {
    if (!token) {
      setState('invalid');
      return;
    }

    setState('loading');
    void getPasswordResetRequest(token)
      .then((result) => {
        setEmail(result.email);
        setState('ready');
      })
      .catch((error: unknown) => {
        // 400 is a link that is unknown, used or expired. Anything else leaves the link as it
        // was, so it is worth trying again.
        const linkRejected = error instanceof ApiError && error.status === 400;
        setState(linkRejected ? 'invalid' : 'unavailable');
      });
  }, [token]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    load();
  }, [load]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) return;

    const nextErrors: FieldErrors = {
      password: passwordError(password),
      passwordConfirmation: passwordConfirmationError(password, passwordConfirmation),
    };
    setErrors(nextErrors);
    setRequestError(null);

    if (nextErrors.password || nextErrors.passwordConfirmation) return;

    setIsSubmitting(true);
    try {
      const session = await resetPassword({ token, password });
      const staff = session.user.role !== 'CUSTOMER';
      setIsStaff(staff);

      // Somebody who left the quotation form to recover the password goes back to it.
      if (!staff && hasQuotationDraft()) {
        navigate('/quotation-request', { replace: true });
        return;
      }

      setState('changed');
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        setRequestError(
          'Alcanzaste el límite de intentos. Esperá unos minutos y volvé a intentar.',
        );
      } else if (
        error instanceof ApiError &&
        error.status === 400 &&
        /enlace/.test(error.message)
      ) {
        // Spent or expired while the page was open.
        setState('invalid');
      } else if (error instanceof ApiError && error.status === 400) {
        setErrors({
          password: 'La contraseña no cumple los requisitos. Revisala y volvé a intentar.',
        });
      } else {
        setRequestError('No pudimos cambiar tu contraseña. Volvé a intentar en unos minutos.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <NarrowPage maxWidth={600}>
      <PageHeader
        eyebrow="TU CUENTA DE PHONETEC"
        title={state === 'changed' ? 'Tu contraseña cambió' : 'Elegí una contraseña nueva'}
        description={descriptionByState[state]}
      />
      {state === 'loading' ? (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={24} color="secondary" />
          <Typography>Estamos validando tu enlace...</Typography>
        </Stack>
      ) : null}
      {state === 'unavailable' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Reintentar
            </Button>
          }
        >
          No pudimos validar tu enlace en este momento. Volvé a intentar en unos minutos.
        </Alert>
      ) : null}
      {state === 'invalid' ? (
        <Alert severity="error">
          El enlace no es válido o venció. Podés{' '}
          <Link component={RouterLink} to="/account/forgot-password" underline="always">
            pedir uno nuevo
          </Link>{' '}
          con tu email.
        </Alert>
      ) : null}
      {state === 'changed' ? (
        <SectionCard title="¡Listo!">
          <Stack spacing={2}>
            <Alert severity="success">
              Ya ingresaste con tu contraseña nueva. La anterior dejó de funcionar.
            </Alert>
            {isStaff ? (
              <PrimaryButton type="button" onClick={() => navigate('/admin')}>
                Ir a Administración
              </PrimaryButton>
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <PrimaryButton type="button" onClick={() => navigate('/catalog')}>
                  Ver el catálogo
                </PrimaryButton>
                <Button
                  type="button"
                  color="inherit"
                  onClick={() => navigate('/quotation-request')}
                >
                  Cotizar un celular
                </Button>
              </Stack>
            )}
          </Stack>
        </SectionCard>
      ) : null}
      {state === 'ready' ? (
        <SectionCard title="Contraseña nueva" description={`Para la cuenta ${email}.`}>
          <Stack component="form" spacing={2} onSubmit={handleSubmit} noValidate>
            {requestError ? <Alert severity="error">{requestError}</Alert> : null}
            <TextField
              label="Contraseña nueva"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setErrors((current) => ({ ...current, password: undefined }));
              }}
              error={Boolean(errors.password)}
              helperText={
                errors.password ||
                'Mínimo 10 caracteres, con una mayúscula, un número y un carácter especial.'
              }
              fullWidth
            />
            <TextField
              label="Repetir contraseña nueva"
              type="password"
              autoComplete="new-password"
              value={passwordConfirmation}
              onChange={(event) => {
                setPasswordConfirmation(event.target.value);
                setErrors((current) => ({ ...current, passwordConfirmation: undefined }));
              }}
              error={Boolean(errors.passwordConfirmation)}
              helperText={errors.passwordConfirmation}
              fullWidth
            />
            <PrimaryButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : 'Guardar contraseña'}
            </PrimaryButton>
          </Stack>
        </SectionCard>
      ) : null}
    </NarrowPage>
  );
}
